/**
 * @file refrigerant-calculations.ts
 * @description Stateless refrigerant calculation functions using CoolProp data
 */

import { AncillaryFunction, FluidData, PressureUnit, PressureValue, RefrigerantState, TemperatureUnit, TemperatureValue } from "../types";
import { convertDeltaTempFromK, convertPressureToPa, convertTempToK } from "./unit-conversions";

function evaluateAncillary(
  equation: AncillaryFunction,
  T: TemperatureValue,
  Tc: TemperatureValue
): number {
  if (!equation.using_tau_r || !equation.T_r) {
    throw new Error("Ancillary function is missing required T_r or using_tau_r");
  }
  
  const tau = equation.using_tau_r ? equation.T_r / T.temperature : Tc.temperature / T.temperature;
  let sum = 0;
  
  if (!equation.n || !equation.t) {
    throw new Error("Ancillary function is missing required n or t");
  }

  // For pV/pL equations, we need to use theta instead of tau based on the description
  const useTheta = equation.type === "pV" || equation.type === "pL" || equation.type === "rhoV" || equation.type === "rhoLnoexp";
  const theta = useTheta ? (1 - 1/tau) : tau;

  for (let i = 0; i < equation.n.length; i++) {
    const base = useTheta ? theta : tau;
    const term = equation.n[i] * Math.pow(base, equation.t[i]);
    sum += term;
  }
  
  // Apply appropriate transformation based on equation type
  if (equation.type === "rational_polynomial") {
    return Math.exp(sum);
  } else if (equation.type === "pV" || equation.type === "pL" || equation.type === "rhoV") {
    // For pressure and density vapor equations: pc*exp(Tc/T*sum(...)) = pc*exp(tau*sum(...))
    const reducingValue = equation.reducing_value || 1;
    const result = Math.exp(tau * sum) * reducingValue;
    return result;
  } else if (equation.type === "rhoLnoexp") {
    // For liquid density without exp, apply (1 + sum) * reducing_value
    const reducingValue = equation.reducing_value || 1;
    return (1 + sum) * reducingValue;
  } else {
    // Default case: just multiply by reducing value
    const reducingValue = equation.reducing_value || 1;
    return sum * reducingValue;
  }
}

// Function to find saturation temperature at given pressure using the Newton-Raphson iteration method
export function findSaturationTemperature(
  fluid: FluidData,
  pressure: PressureValue,
  tolerance: number = 1e-6,
  maxIterations: number = 100
): TemperatureValue | null {
  // Check for either pS (single function) or pL/pV (separate functions)
  const pS = fluid.ANCILLARIES?.pS || fluid.ANCILLARIES?.pL || fluid.ANCILLARIES?.pV;
  
  if (!pS || !fluid.STATES?.critical) {
    throw new Error("Required fluid data not available");
  }
  
  const Tc = { temperature: fluid.STATES.critical.T, temperatureUnit: TemperatureUnit.K };
  const pc = { pressure: fluid.STATES.critical.p, pressureUnit: PressureUnit.PAA };
  
  // Convert pressure to Pa if needed
  const pressurePa = convertPressureToPa(pressure);
  
  if (!pS.Tmin || !pS.Tmax) {
    throw new Error("Ancillary function is missing required Tmin or Tmax");
  }

  // Check if pressure is above critical pressure
  if (pressurePa.pressure > pc.pressure) {
    throw new Error(`Pressure ${pressurePa.pressure} Pa is above critical pressure ${pc.pressure} Pa`);
  }

  // Check if pressure is too low (below triple point if available)
  if (fluid.STATES?.triple_liquid && pressurePa.pressure < fluid.STATES.triple_liquid.p) {
    throw new Error(`Pressure ${pressurePa.pressure} Pa is below triple point pressure ${fluid.STATES.triple_liquid.p} Pa`);
  }

  // Ensure T is within bounds
  const Tmin = pS.Tmin;
  const Tmax = Math.min(pS.Tmax, Tc.temperature * 0.9999); // Stay slightly below critical
  
  // Better initial guess using reduced pressure correlation
  let T_current: number;
  const pr = pressurePa.pressure / pc.pressure;
  
  // Use different correlations based on pressure range
  if (pr > 0.9) {
    // Very close to critical
    T_current = Tc.temperature * (0.95 + 0.05 * pr);
  } else if (pr > 0.1) {
    // Mid-range pressure - use Antoine-like correlation
    T_current = Tc.temperature * (0.4 + 0.6 * Math.pow(pr, 0.3));
  } else {
    // Low pressure - use Clausius-Clapeyron type correlation
    T_current = Tc.temperature / (1 - 0.3 * Math.log(pr));
  }
  
  // Ensure initial guess is within bounds
  T_current = Math.max(Tmin, Math.min(T_current, Tmax));
  
  // Keep track of bounds for bisection fallback
  let T_lower = Tmin;
  let T_upper = Tmax;
  
  // Newton-Raphson iteration with bisection fallback
  for (let iter = 0; iter < maxIterations; iter++) {
    const T_obj = { temperature: T_current, temperatureUnit: TemperatureUnit.K };
    const pCalc = evaluateAncillary(pS, T_obj, Tc);
    const error = pCalc - pressurePa.pressure;
    const relError = Math.abs(error / pressurePa.pressure);
    
    // Update bounds based on error sign
    if (error > 0) {
      // Calculated pressure too high, temperature must be lower
      T_upper = T_current;
    } else {
      // Calculated pressure too low, temperature must be higher
      T_lower = T_current;
    }
    
    // Check convergence
    if (relError < tolerance) {
      return T_obj;
    }
    
    // Calculate numerical derivative
    const dT = Math.max(T_current * 0.0001, 0.01); // Use relative step size with minimum
    const T_plus = { temperature: Math.min(T_current + dT, Tmax), temperatureUnit: TemperatureUnit.K };
    const pCalcPlus = evaluateAncillary(pS, T_plus, Tc);
    const dpdT = (pCalcPlus - pCalc) / dT;
    
    // Decide whether to use Newton-Raphson or bisection
    let useNewtonRaphson = true;
    let newT = T_current;
    
    if (Math.abs(dpdT) < 1e-10) {
      // Derivative too small, use bisection
      useNewtonRaphson = false;
    } else {
      // Calculate Newton-Raphson step
      const deltaT = -error / dpdT;
      newT = T_current + deltaT;
      
      // Check if Newton-Raphson step is reasonable
      if (newT <= T_lower || newT >= T_upper || Math.abs(deltaT) > 0.3 * T_current) {
        useNewtonRaphson = false;
      }
    }
    
    if (!useNewtonRaphson) {
      // Use bisection
      newT = (T_lower + T_upper) / 2;
      
      // Check if bisection is making progress
      if (Math.abs(newT - T_current) < 1e-10) {
        throw new Error(`Bisection stalled at iteration ${iter}`);
      }
    }
    
    T_current = newT;
    
    // Ensure T stays within bounds (should already be, but just in case)
    T_current = Math.max(T_lower, Math.min(T_current, T_upper));
  }
  
  throw new Error(`Failed to converge after ${maxIterations} iterations. Last T=${T_current.toFixed(3)}K, target pressure=${pressurePa.pressure.toExponential(3)}Pa`);
}

export function calculateSuperheat(
  fluid: FluidData,
  actualTemperature: TemperatureValue,
  pressure: PressureValue
): TemperatureValue {
  const tempK = convertTempToK(actualTemperature);
  const pressurePa = convertPressureToPa(pressure);
  
  const saturationTemp = findSaturationTemperature(fluid, pressurePa);
  
  if (saturationTemp === null) {
    throw new Error("Could not determine saturation temperature");
  }
  
  const superheat = tempK.temperature - saturationTemp.temperature;
  
  if (superheat < 0) {
    throw new Error("Negative superheat indicates subcooled liquid, not superheated vapor");
  }
  
  return convertDeltaTempFromK(superheat, actualTemperature.temperatureUnit);
}

export function calculateSubcooling(
  fluid: FluidData,
  actualTemperature: TemperatureValue,
  pressure: PressureValue
): TemperatureValue {
  const tempK = convertTempToK(actualTemperature);
  const pressurePa = convertPressureToPa(pressure);
  
  const saturationTemp = findSaturationTemperature(fluid, pressurePa);
  
  if (saturationTemp === null) {
    throw new Error("Could not determine saturation temperature");
  }
  
  const subcooling = saturationTemp.temperature - tempK.temperature;
  
  if (subcooling < 0) {
    throw new Error("Negative subcooling indicates superheated vapor, not subcooled liquid");
  }
  
  return convertDeltaTempFromK(subcooling, actualTemperature.temperatureUnit);
}

export function calculateSuperheatOrSubcooling(
  fluid: FluidData,
  actualTemperature: TemperatureValue,
  pressure: PressureValue
): {
  value: TemperatureValue;
  type: RefrigerantState;
} {
  const tempUnitUsed = actualTemperature.temperatureUnit;
  const convertedTemperature = convertTempToK(actualTemperature);
  const convertedPressure = convertPressureToPa(pressure);
  
  const saturationTemp = findSaturationTemperature(fluid, convertedPressure);
  
  if (saturationTemp === null) {
    throw new Error("Could not determine saturation temperature");
  }
  
  const difference = convertedTemperature.temperature - saturationTemp.temperature;
  const tolerance = 0.1; // K
  
  if (Math.abs(difference) < tolerance) {
    return { value: { temperature: 0, temperatureUnit: tempUnitUsed }, type: RefrigerantState.Saturated };
  } else if (difference > 0) {
    return { value: convertDeltaTempFromK(difference, tempUnitUsed), type: RefrigerantState.Superheated };
  } else {
    return { value: convertDeltaTempFromK(-difference, tempUnitUsed), type: RefrigerantState.Subcooled };
  }
}